import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "./DashboardPolicies.css";

const POLICY_IDS = ["shipping", "returns", "privacy", "terms"];
const POLICY_LABELS = {
  shipping: { icon: "fa-solid fa-truck", label: "Shipping & Installation", arLabel: "الشحن والتركيب" },
  returns:  { icon: "fa-solid fa-rotate-left", label: "Returns & Exchanges", arLabel: "الإرجاع والاستبدال" },
  privacy:  { icon: "fa-solid fa-shield-halved", label: "Privacy Policy", arLabel: "سياسة الخصوصية" },
  terms:    { icon: "fa-solid fa-file-contract", label: "Terms & Privacy", arLabel: "الاستخدام والخصوصية" },
};

function emptyPolicy(id) {
  return { policyId: id, ar: { title: "", body: "" }, en: { title: "", body: "" } };
}

function applyFormat(taRef, currentValue, onChange, type) {
  const ta = taRef.current;
  if (!ta) return;

  const start = ta.selectionStart;
  const end = ta.selectionEnd;
  const selected = currentValue.substring(start, end);
  let newValue = currentValue;
  let newStart = start;
  let newEnd = end;

  if (type === "bold") {
    if (selected) {
      newValue = currentValue.substring(0, start) + `**${selected}**` + currentValue.substring(end);
      newEnd = end + 4;
    } else {
      newValue = currentValue.substring(0, start) + `****` + currentValue.substring(end);
      newStart = start + 2;
      newEnd = start + 2;
    }
  } else if (type === "bullet") {
    const lineStart = currentValue.lastIndexOf("\n", start - 1) + 1;
    newValue = currentValue.substring(0, lineStart) + "• " + currentValue.substring(lineStart);
    newStart = start + 2;
    newEnd = end + 2;
  } else if (type === "heading") {
    const lineStart = currentValue.lastIndexOf("\n", start - 1) + 1;
    const lineEndRaw = currentValue.indexOf("\n", start);
    const lineEnd = lineEndRaw === -1 ? currentValue.length : lineEndRaw;
    const lineContent = currentValue.substring(lineStart, lineEnd);
    if (lineContent.startsWith("## ")) {
      newValue = currentValue.substring(0, lineStart) + lineContent.substring(3) + currentValue.substring(lineEnd);
      newStart = Math.max(lineStart, start - 3);
      newEnd = Math.max(lineStart, end - 3);
    } else {
      newValue = currentValue.substring(0, lineStart) + "## " + currentValue.substring(lineStart);
      newStart = start + 3;
      newEnd = end + 3;
    }
  } else if (type === "newline") {
    newValue = currentValue.substring(0, start) + "\n" + currentValue.substring(end);
    newStart = start + 1;
    newEnd = start + 1;
  }

  onChange(newValue);
  requestAnimationFrame(() => {
    ta.selectionStart = newStart;
    ta.selectionEnd = newEnd;
    ta.focus();
  });
}

function Toolbar({ taRef, value, onChange }) {
  const btn = (type, icon, title) => (
    <button
      type="button"
      className="dash-policy-toolbar-btn"
      title={title}
      onMouseDown={(e) => { e.preventDefault(); applyFormat(taRef, value, onChange, type); }}
    >
      {icon}
    </button>
  );

  return (
    <div className="dash-policy-toolbar">
      {btn("heading", <><i className="fa-solid fa-heading" /> H2</>, "Section heading (##)")}
      <span className="dash-policy-toolbar-sep" />
      {btn("bold", <strong>B</strong>, "Bold (**text**)")}
      {btn("bullet", "•", "Bullet point (•)")}
      {btn("newline", <i className="fa-solid fa-turn-down" />, "New line")}
    </div>
  );
}

export default function DashboardPolicies() {
  const [policies, setPolicies] = useState({});
  const [selected, setSelected] = useState("shipping");
  const [form, setForm] = useState({ ar: { title: "", body: "" }, en: { title: "", body: "" } });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const arRef = useRef(null);
  const enRef = useRef(null);

  const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    setLoading(true);
    axios.get("/store/policies")
      .then((res) => {
        const map = {};
        (res.data || []).forEach((p) => { map[p.policyId] = p; });
        setPolicies(map);
        const first = map["shipping"] || emptyPolicy("shipping");
        setForm({ ar: { ...first.ar }, en: { ...first.en } });
      })
      .catch(() => toast.error("Failed to load policies"))
      .finally(() => setLoading(false));
  }, []);

  const selectPolicy = useCallback((id) => {
    setSelected(id);
    const p = policies[id] || emptyPolicy(id);
    setForm({ ar: { title: p.ar?.title || "", body: p.ar?.body || "" }, en: { title: p.en?.title || "", body: p.en?.body || "" } });
  }, [policies]);

  const handleChange = (lang, field, value) => {
    setForm((prev) => ({ ...prev, [lang]: { ...prev[lang], [field]: value } }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`/store/policies/${selected}`, form, { headers });
      setPolicies((prev) => ({ ...prev, [selected]: res.data }));
      toast.success("Policy saved successfully");
    } catch {
      toast.error("Failed to save policy");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dash-policies">
      <div className="dash-policies-header">
        <h1 className="dash-policies-title">
          <i className="fa-solid fa-file-lines"></i> Policies Control
        </h1>
      </div>

      <div className="dash-policies-layout">
        <aside className="dash-policies-sidebar">
          {POLICY_IDS.map((id) => {
            const meta = POLICY_LABELS[id];
            return (
              <button
                key={id}
                className={`dash-policy-tab${selected === id ? " is-active" : ""}`}
                onClick={() => selectPolicy(id)}
              >
                <i className={meta.icon}></i>
                <span className="dash-policy-tab-labels">
                  <span className="dash-policy-tab-en">{meta.label}</span>
                  <span className="dash-policy-tab-ar">{meta.arLabel}</span>
                </span>
              </button>
            );
          })}
        </aside>

        <div className="dash-policies-editor">
          {loading ? (
            <div className="dash-policies-loading">
              <i className="fa-solid fa-circle-notch fa-spin"></i> Loading…
            </div>
          ) : (
            <>
              <div className="dash-policies-fields">
                <div className="dash-policy-field-group">
                  <label className="dash-policy-label">Title (AR) — العنوان بالعربي</label>
                  <input
                    className="dash-policy-input"
                    dir="rtl"
                    value={form.ar.title}
                    onChange={(e) => handleChange("ar", "title", e.target.value)}
                    placeholder="عنوان السياسة بالعربي"
                  />
                </div>
                <div className="dash-policy-field-group">
                  <label className="dash-policy-label">Title (EN)</label>
                  <input
                    className="dash-policy-input"
                    value={form.en.title}
                    onChange={(e) => handleChange("en", "title", e.target.value)}
                    placeholder="Policy title in English"
                  />
                </div>
              </div>

              <div className="dash-policies-bodies">
                <div className="dash-policy-body-col">
                  <label className="dash-policy-label">Body (AR) — المحتوى بالعربي</label>
                  <Toolbar
                    taRef={arRef}
                    value={form.ar.body}
                    onChange={(v) => handleChange("ar", "body", v)}
                  />
                  <textarea
                    ref={arRef}
                    className="dash-policy-textarea"
                    dir="rtl"
                    value={form.ar.body}
                    onChange={(e) => handleChange("ar", "body", e.target.value)}
                    placeholder="محتوى السياسة — كل سطر فقرة — ## للعنوان — **كلمة** للبولد — • للنقطة"
                  />
                  <span className="dash-policy-line-count">
                    {form.ar.body.split("\n").filter(Boolean).length} سطر
                  </span>
                </div>
                <div className="dash-policy-body-col">
                  <label className="dash-policy-label">Body (EN) — English content</label>
                  <Toolbar
                    taRef={enRef}
                    value={form.en.body}
                    onChange={(v) => handleChange("en", "body", v)}
                  />
                  <textarea
                    ref={enRef}
                    className="dash-policy-textarea"
                    value={form.en.body}
                    onChange={(e) => handleChange("en", "body", e.target.value)}
                    placeholder="Policy content — each line is a paragraph — ## for heading — **word** for bold — • for bullet"
                  />
                  <span className="dash-policy-line-count">
                    {form.en.body.split("\n").filter(Boolean).length} lines
                  </span>
                </div>
              </div>

              <div className="dash-policies-actions">
                <button className="dash-policy-save-btn" onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <><i className="fa-solid fa-circle-notch fa-spin"></i> Saving…</>
                  ) : (
                    <><i className="fa-solid fa-floppy-disk"></i> Save Policy</>
                  )}
                </button>
                <a
                  href={`/store/policies/${selected}`}
                  target="_blank"
                  rel="noreferrer"
                  className="dash-policy-preview-link"
                >
                  <i className="fa-solid fa-arrow-up-right-from-square"></i> Preview
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
